import { useEffect, useRef, useState } from "react"
import DatalakeService from "src/services/datalake-service"
import { getCnpjRoot } from "src/v2/utils/cnpj-root"
import { buildFinancialSeries, normalizeBalancePayload } from "./company-financial-data"

const GROUPS = ["Consolidado", "Individual"]
const EMPTY_DATA = {}

const initialState = {
    status: "idle",
    error: null,
    availableGroups: [],
    dataByGroup: EMPTY_DATA,
}

const normalizeRootCnpj = (value) => {
    const digits = String(value ?? "").replace(/\D/g, "")

    return digits.length === 8 ? digits : getCnpjRoot(digits)
}

const hasDisplayableMetrics = (records) => {
    const { balanceRows, indicatorRows } = buildFinancialSeries(records)

    return balanceRows.length > 0 || indicatorRows.length > 0
}

export const useCompanyFinancialData = ({ raizCnpj }) => {
    const serviceRef = useRef(new DatalakeService())
    const [state, setState] = useState(initialState)
    const [selectedGroup, setSelectedGroup] = useState("")
    const [reloadKey, setReloadKey] = useState(0)
    const normalizedRootCnpj = normalizeRootCnpj(raizCnpj)

    useEffect(() => {
        let active = true

        if (!normalizedRootCnpj) {
            setState(initialState)
            setSelectedGroup("")
            return undefined
        }

        setState({
            status: "loading",
            error: null,
            availableGroups: [],
            dataByGroup: EMPTY_DATA,
        })
        setSelectedGroup("")

        const load = async () => {
            const results = await Promise.allSettled(
                GROUPS.map((group) => serviceRef.current.getIndicesBalanco(normalizedRootCnpj, group))
            )

            if (!active) return

            const dataByGroup = GROUPS.reduce((currentData, group, index) => {
                const result = results[index]

                if (result.status !== "fulfilled") return currentData

                const normalizedData = normalizeBalancePayload(result.value?.body?.data)

                return hasDisplayableMetrics(normalizedData.records)
                    ? { ...currentData, [group]: normalizedData }
                    : currentData
            }, {})
            const availableGroups = GROUPS.filter((group) => Boolean(dataByGroup[group]))
            const allRequestsFailed = results.every((result) => result.status === "rejected")

            if (allRequestsFailed) {
                setState({
                    status: "error",
                    error: "Não foi possível carregar os indicadores financeiros.",
                    availableGroups: [],
                    dataByGroup: EMPTY_DATA,
                })
                return
            }

            setState({
                status: availableGroups.length > 0 ? "success" : "empty",
                error: null,
                availableGroups,
                dataByGroup,
            })
            setSelectedGroup(availableGroups[0] ?? "")
        }

        void load()

        return () => {
            active = false
        }
    }, [normalizedRootCnpj, reloadKey])

    return {
        ...state,
        selectedGroup,
        selectGroup: setSelectedGroup,
        reload: () => setReloadKey((currentKey) => currentKey + 1),
    }
}
